/**
 * ComingSoonPage — 统一的 Coming Soon 占位页面
 *
 * 用于尚未开放的功能 tab，保持品牌一致性。
 * 支持 reduced motion 偏好。
 */
import { motion, useReducedMotion } from 'framer-motion';
import type { LucideIcon } from 'lucide-react';
import { PageHeader } from './PageHeader';
import { FONTS } from '../lib/theme';
import styles from './ComingSoonPage.module.css';

const ZEN = [0.2, 0.8, 0.2, 1] as const;

const THEME = {
  clay: {
    color: 'var(--color-clay)',
    bg: 'var(--color-clay-8)',
    border: 'var(--color-clay-20)',
  },
  celadon: {
    color: 'var(--color-celadon)',
    bg: 'var(--color-celadon-8)',
    border: 'var(--color-celadon-20)',
  },
} as const;

interface ComingSoonPageProps {
  title: string;
  tagline: string;
  icon: LucideIcon;
  theme: 'clay' | 'celadon';
}

export function ComingSoonPage({ title, tagline, icon: Icon, theme }: ComingSoonPageProps) {
  const shouldReduce = useReducedMotion();
  const t = THEME[theme];

  return (
    <div className={styles.container}>
      <PageHeader mode="primary" title={title} subtitle="即将开放" showProfile profileHref="/profile" />

      <div className={styles.content}>
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, ease: ZEN }}
          className={styles.card}
        >
          {/* 图标舞台 */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15, ease: ZEN }}
            className={styles.iconStage}
          >
            {!shouldReduce && (
              <>
                <motion.div
                  className={styles.glow}
                  style={{ background: `radial-gradient(circle, ${t.bg} 0%, transparent 70%)` }}
                  animate={{ opacity: [0.3, 0.7, 0.3], scale: [0.8, 1.3, 0.8] }}
                  transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
                />
                {[0, 1, 2].map(i => (
                  <motion.span
                    key={i}
                    className={styles.particle}
                    style={{ background: t.color }}
                    animate={{
                      y: [0, -20 - i * 8, 0],
                      x: [0, (i - 1) * 12, 0],
                      opacity: [0, 0.4, 0],
                      scale: [0.5, 1, 0.5],
                    }}
                    transition={{
                      duration: 3 + i * 0.8,
                      repeat: Infinity,
                      ease: 'easeInOut',
                      delay: i * 0.6,
                    }}
                  />
                ))}
              </>
            )}

            <motion.div
              className={styles.iconBox}
              style={{ background: t.bg, borderColor: t.border }}
              animate={shouldReduce ? undefined : { y: [0, -4, 0] }}
              transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
            >
              <Icon size={32} color={t.color} strokeWidth={1.2} />
            </motion.div>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.25, ease: ZEN }}
            className={styles.title}
            style={{ fontFamily: FONTS.serif }}
          >
            Coming Soon
          </motion.h2>

          <motion.div
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.6, delay: 0.35, ease: ZEN }}
            className={styles.divider}
            style={{ background: t.border }}
          >
            {!shouldReduce && (
              <motion.div
                className={styles.dividerShimmer}
                style={{ background: t.color }}
                animate={{ x: ['-100%', '200%'] }}
                transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut', delay: 1.5 }}
              />
            )}
          </motion.div>

          <motion.p
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.45, ease: ZEN }}
            className={styles.desc}
          >
            我们正在精心打磨这个功能，敬请期待。
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.55, ease: ZEN }}
            className={styles.hint}
          >
            {tagline}
          </motion.p>
        </motion.div>
      </div>
    </div>
  );
}
